import { Mui, MuiIcons } from '@osu/react-ui';
import { useReducer } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";
import { useGetDashboardDataQuery } from '../services/graphql/api';
import { selectDashboardData } from '../services/graphql/slices/dashboard-slice';
import InProgressTable from './components/inprogress-table';
import { PullCard } from './components/pull-card';
import { tableFilterReducer } from './components/table-filter-reducer';
import ToggleButtonGroup from './components/toggle-button-group';
import { DASHBOARD_BUTTON_GROUP, PROGRAMS, AUTH_ROLES } from '../util/constants';
import useRoleAuthentication from '../Authentication/hooks/useRoleAuthentication';
import { resolveDefaultProgramSide } from '../util/scholars/scholarsScope';
import { getPreferredProgramSide } from '../util/scholars/preferredProgramSide';

// Roles the getDashboardDetails query authorizer allows (schema.graphql). Only these should fire it;
// anyone else (Scholars-only staff, Honors reviewers) is redirected away and would otherwise 401.
const HONORS_DASHBOARD_ROLES = [
  AUTH_ROLES.ADMIN_ACCESS,
  AUTH_ROLES.READ_ACCESS,
  AUTH_ROLES.HONORS_SEASONAL_MANAGER,
  AUTH_ROLES.HONORS_PROGRAM_COORDINATOR,
];

const INITIAL_FILTER_STATE = {
  [DASHBOARD_BUTTON_GROUP.RESUMELATER.KEY]: true,
  [DASHBOARD_BUTTON_GROUP.ASSIGNED.KEY]: true,
};

const Dashboard = () => {
  const data = useSelector(selectDashboardData)
  const [filterState, dispatch] = useReducer(tableFilterReducer, INITIAL_FILTER_STATE);

  // Auto-redirect student workers to basic search
  const nav = useNavigate();
  const location = useLocation();
  const authDetails = useRoleAuthentication({autoMode: true, redirectOnUnauthorized: true });
  
  // Only fire the Honors dashboard query for users its authorizer allows. Scholars-only staff and Honors
  // reviewers land on "/" briefly before being redirected to their side; firing it for them 401s.
  const canSeeHonorsDashboard = (authDetails.roles ?? []).some(r => HONORS_DASHBOARD_ROLES.includes(r));
  const { isLoading } = useGetDashboardDataQuery(undefined, { 
    refetchOnMountOrArgChange: true,
    skip: !(authDetails.roles?.length > 0) || authDetails.isStudent || !canSeeHonorsDashboard
  });
  
  if (authDetails.isStudent) { nav("/search"); return; }

  // has543 — "/" is the Honors dashboard (the Honors landing). Route users to the correct side on
  // landing based on their roles + persisted preference:
  //   - Scholars-only users  → Scholars side (/scholars/queue). They have NO toggle, so this MUST run
  //     on every arrival at "/" or they'd be stranded on the Honors dashboard.
  //   - Dual-admins          → their stored preference, defaulting to Honors.
  //   - Honors-only / default → stay here (Honors).
  //
  // The redirect must fire on a genuine landing (fresh login, page refresh, direct nav) but NOT when the
  // user deliberately clicked the Honors program toggle — otherwise a Scholars-preference dual-admin can
  // never reach Honors (the toggle navigates to "/", which would re-apply the preference and snap them
  // back — the flicker). The Honors toggle signals intent via location.state.programSwitch; when that is
  // present we honor the manual switch and skip the preference redirect. This is per-navigation intent,
  // not session state, so refresh/login still correctly re-apply the preference.
  const wantsScholars = resolveDefaultProgramSide(authDetails.roles, getPreferredProgramSide()) === PROGRAMS.SCHOLARS.KEY;
  const isManualProgramSwitch = location.state?.programSwitch === true;
  if (wantsScholars && !isManualProgramSwitch) {
    nav("/scholars/queue");
    return;
  }
  
  const filteredTableData = data?.inProgressReviews?.filter(row => {
    if (filterState.RESUMELATER && row.resumeLater) return true;
    if (filterState.ASSIGNED && row.manuallyAssigned) return true;
    return !filterState.RESUMELATER && !filterState.ASSIGNED;
  });

  const CardSkeleton = () => {
    return (
      <Mui.Grid item xs={12} sm={12} md={12} lg={12}>
        <Mui.Skeleton height={300} />
      </Mui.Grid>
    )
  }
  // console.log(data)
  return (
    <>
      <Mui.Box style={{ display: 'flex', flexDirection: 'column' }}>
        <Mui.Box mb={2}>
          <Mui.Grid container styles={{ flexGrow: '1' }} spacing={2}>
            {isLoading ? <CardSkeleton /> : data?.cards?.map((card) => {
              return (
                <Mui.Grid key={card.key} item xs={12} sm={6} md={6} lg={4}>
                  <PullCard card={card} />
                </Mui.Grid>
              )
            })}
          </Mui.Grid>
        </Mui.Box>

        <Mui.Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Mui.Accordion defaultExpanded={true}>
            <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />} id={'1'} style={{ fontSize: '1rem' }}>
              <Mui.Typography sx={{ fontSize: "1.265rem", fontWeight: "bold" }}>In Progress</Mui.Typography>
            </Mui.AccordionSummary>
            <Mui.AccordionDetails>
              <Mui.Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <ToggleButtonGroup dispatch={dispatch} filterState={filterState} data={data} />
                <InProgressTable isLoading={isLoading} filteredTableData={filteredTableData} />
              </Mui.Box>
            </Mui.AccordionDetails>
          </Mui.Accordion>
        </Mui.Box >
      </Mui.Box >
    </>
  )
}

export default Dashboard